import { useCallback, useMemo, useState } from "react";
import type { QuinnMessage } from "../types";
import { extractArtifacts, type QuinnArtifact } from "../utils/artifacts.ts";
import type { QuinnArtifactCanvas } from "../components/Canvas";

export type QuinnCanvasState = {
    artifacts: QuinnArtifact[];
    activeArtifact: QuinnArtifact | null;
    isOpen: boolean;
    close: () => void;
    toggle: () => void;
    canvas: QuinnArtifactCanvas;
};

type Tracked = {
    chatId: string | null;
    messages: QuinnMessage[];
    seenArtifactIds: ReadonlySet<string>;
};

type CanvasView = {
    isOpen: boolean;
    activeArtifactId: string | null;
};

const CLOSED: CanvasView = { isOpen: false, activeArtifactId: null };

export function useQuinnCanvasState(
    chatId: string | null,
    messages: QuinnMessage[],
    requestedArtifactId: string | null = null,
): QuinnCanvasState {
    const artifacts = useMemo(() => extractArtifacts(messages), [messages]);
    const [tracked, setTracked] = useState<Tracked>(() => ({
        chatId,
        messages,
        seenArtifactIds: new Set(artifacts.map((artifact) => artifact.id)),
    }));
    const [view, setView] = useState<CanvasView>(() =>
        requestedArtifactId ? { isOpen: true, activeArtifactId: requestedArtifactId } : CLOSED,
    );

    if (tracked.messages !== messages || tracked.chatId !== chatId) {
        const renderedMessageIds = new Set(tracked.messages.map((message) => message.id));
        const seenArtifactIds = new Set(tracked.seenArtifactIds);
        let arrived: QuinnArtifact | null = null;
        for (const artifact of artifacts) {
            if (seenArtifactIds.has(artifact.id)) continue;
            seenArtifactIds.add(artifact.id);
            if (renderedMessageIds.has(artifact.messageId)) arrived = artifact;
        }
        setTracked({ chatId, messages, seenArtifactIds });
        if (arrived) {
            setView({ isOpen: true, activeArtifactId: arrived.id });
        } else if (tracked.chatId !== chatId) {
            setView(CLOSED);
        }
    }

    const openArtifact = useCallback((artifactId: string) => {
        setView({ isOpen: true, activeArtifactId: artifactId });
    }, []);
    const close = useCallback(() => setView((current) => ({ ...current, isOpen: false })), []);

    const activeArtifact =
        artifacts.find((artifact) => artifact.id === view.activeArtifactId) ?? artifacts[artifacts.length - 1] ?? null;
    const hasArtifacts = artifacts.length > 0;

    const toggle = useCallback(() => {
        setView((current) => ({ ...current, isOpen: !current.isOpen && hasArtifacts }));
    }, [hasArtifacts]);

    const isOpen = view.isOpen && activeArtifact !== null;
    const canvas = useMemo<QuinnArtifactCanvas>(
        () => ({ activeArtifactId: isOpen ? (activeArtifact?.id ?? null) : null, openArtifact }),
        [isOpen, activeArtifact?.id, openArtifact],
    );

    return { artifacts, activeArtifact, isOpen, close, toggle, canvas };
}
